import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { PlusCircle, MinusCircle, Share2, Star } from 'lucide-react';
import OptionsPremiumChartModal from './OptionsPremiumChartModal';
import SharePreviewModal from './SharePreviewModal';
import WatchlistAssignGroupsModal from './WatchlistAssignGroupsModal';
import { saveContract, getWatchlistGroups, assignContractsToGroup } from '../services/api';
import { useLastClickedTicker } from '../contexts/LastClickedTickerContext';
import { SimulationSettingsPanel } from './shared';
import WatchlistHeatmapView from './WatchlistHeatmapView';
import WatchlistFilterControls from './WatchlistFilterControls';
import { recalculateSimulation } from '../utils/blackScholes';
import {
  BASELINE_MODES,
  DEFAULT_BASELINE_MODE,
  applyBaselineToRow,
  resolveBaseline,
  getStartingPriceColumnLabel,
  getStartingPriceTooltip,
} from '../utils/watchlistBaseline';
import { useIsMobile } from '../hooks/useIsMobile';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

// Column groups for preset mode (multiple ±% scenarios)
const PRESET_COLUMN_GROUPS = {
  All: [
    "Ticker", "Position Type", "Option Type", "Strike",
    "Expiration", "Current Premium", "Days to Target", "Underlying Scenario % Change",
    "Current Underlying", "Simulated Underlying (+)", "Simulated Premium (+)", "Simulated Premium (+) % Change",
    "Equity Invested", "Simulated Equity (+)",
     "Simulated Underlying (-)",
    "Simulated Premium (-)", "Simulated Premium (-) % Change", "Simulated Equity (-)",
    "Number of Contracts", "Average Cost per Contract",
    "Collateral Required", "Return on Collateral %", "Max Profit", "Max Loss",
    "Simulated P&L (+)", "Simulated P&L (-)",
    "Ann. Return (Target) (+)", "Ann. Return (Target) (-)",
    "Ann. Return (Expiry) (+)", "Ann. Return (Expiry) (-)",
    "Volume", "Open Interest",
    "Implied Volatility", "Delta", "Theta", "Gamma", "Vega", "Rho"
  ],
  Simulation: [
    "Days to Target", "Underlying Scenario % Change", "Current Underlying",
    "Simulated Underlying (+)",
    "Current Premium",
    "Simulated Premium (+)", "Simulated Premium (+) % Change",
    "Ann. Return (Target) (+)", "Ann. Return (Expiry) (+)",
    "Equity Invested", "Simulated Equity (+)",
    "Simulated Underlying (-)", "Simulated Premium (-)", "Simulated Premium (-) % Change",
    "Ann. Return (Target) (-)", "Ann. Return (Expiry) (-)",
     "Simulated Equity (-)"
  ],
  "Sim Short": [
    "Days to Target", "Underlying Scenario % Change",
    "Current Underlying", "Simulated Underlying (+)", "Simulated Premium (+) % Change",
    "Simulated Underlying (-)", "Simulated Premium (-) % Change"
  ],
  "Sell Metrics": [
    "Current Underlying", "Collateral Required", "Return on Collateral %",
    "Days to Target", "Simulated Underlying (+)", "Simulated Premium (+) % Change", "Simulated P&L (+)",
    "Simulated Underlying (-)", "Simulated Premium (-) % Change", "Simulated P&L (-)",
    "Max Profit", "Max Loss"
  ],
  "Position Details": [
    "Current Underlying",
    "Number of Contracts", "Average Cost per Contract",
    "Equity Invested"
  ],
  "Market Data": [
    "Volume", "Open Interest"
  ],
  Greeks: [
    "Implied Volatility", "Delta", "Theta", "Gamma", "Vega", "Rho"
  ]
};

// Column groups for custom mode (single scenario with sliders)
const CUSTOM_COLUMN_GROUPS = {
  All: [
    "Ticker", "Position Type", "Option Type", "Strike", "Expiration",
    "Current Premium", "Days to Gain", "Underlying Gain %",
    "Underlying Price", "Simulated Underlying", "Simulated Premium", "Premium % Gain",
    "Ann. Return (Target)", "Ann. Return (Expiry)",
    "Equity Invested", "Simulated Equity",
    "Number of Contracts", "Average Cost per Contract",
    "Collateral Required", "Return on Collateral %", "Max Profit", "Max Loss",
    "Simulated P&L",
    "Volume", "Open Interest",
    "Implied Volatility", "Delta", "Theta", "Gamma", "Vega", "Rho",
    "Days Until Expiration"
  ],
  Simulation: [
    "Days to Gain", "Underlying Gain %", "Underlying Price",
    "Simulated Underlying", "Current Premium", "Simulated Premium",
    "Premium % Gain", "Equity Invested", "Simulated Equity",
    "Implied Volatility", "Ann. Return (Target)", "Ann. Return (Expiry)"
  ],
  "Sell Metrics": [
    "Underlying Price", "Collateral Required", "Return on Collateral %",
    "Days to Gain", "Simulated Underlying", "Simulated Premium", "Premium % Gain", "Simulated P&L",
    "Max Profit", "Max Loss"
  ],
  "Position Details": [
    "Current Underlying",
    "Number of Contracts", "Average Cost per Contract",
    "Equity Invested"
  ],
  "Market Data": [
    "Volume", "Open Interest"
  ],
  Greeks: [
    "Implied Volatility", "Delta", "Theta", "Gamma", "Vega", "Rho"
  ]
};

// Legacy reference for backward compatibility
const COLUMN_GROUPS = PRESET_COLUMN_GROUPS;

// Default simulation settings for custom ("Simulation Settings") mode.
// Must match the SimulationSettingsPanel slider defaults so the panel and
// the results table always agree, even before the user touches a slider.
const CUSTOM_SIM_DEFAULT_DAYS = 45;
const CUSTOM_SIM_DEFAULT_GAIN = 10;

const ESSENTIAL_COLUMNS = [
  "Ticker", "Position Type", "Option Type", "Strike", "Current Premium", "Expiration", 'Number of Contracts',
];

// Column header tooltip descriptions
// Keys are raw column names (data keys), values are human-readable descriptions
const COLUMN_HEADER_TOOLTIPS = {
  // Essential/Identity columns
  "Position Type": "Whether you are buying (long) or selling (short) this option contract.",
  "Option Type": "Call options profit when the stock rises. Put options profit when the stock falls.",
  "Strike": "The price at which you can buy (call) or sell (put) the underlying stock if exercised.",
  "Expiration": "The date the option contract expires. After this date, the option becomes worthless if not exercised.",
  "Current Premium": "The current market price (premium) of the option contract per share.",

  // Preset mode simulation columns
  "Days to Target": "Number of days remaining until your exit date. Counts down daily from when you saved the contract.",
  "Underlying Scenario % Change": "The simulated percentage change applied to the underlying stock price for this scenario row.",
  "Current Underlying": "The current market price of the underlying stock.",
  "Simulated Underlying (+)": "Projected underlying price if the stock rises by the scenario percentage.",
  "Simulated Premium (+)": "Projected option premium if the stock rises by the scenario percentage, accounting for time decay.",
  "Simulated Premium (+) % Change": "Percentage change between current premium and the simulated premium in the bullish scenario.",
  "Ann. Return (Target) (+)": "Annualized return if you hold until the exit date in the bullish scenario. Calculated as (% gain / days held) x 365.",
  "Ann. Return (Expiry) (+)": "Annualized return if you hold until expiration in the bullish scenario.",
  "Equity Invested": "Total capital invested: Number of Contracts x Avg Cost per Contract x 100 shares.",
  "Simulated Equity (+)": "Projected total equity value in the bullish scenario.",
  "Simulated Underlying (-)": "Projected underlying price if the stock falls by the scenario percentage.",
  "Simulated Premium (-)": "Projected option premium if the stock falls by the scenario percentage, accounting for time decay.",
  "Simulated Premium (-) % Change": "Percentage change between current premium and the simulated premium in the bearish scenario.",
  "Ann. Return (Target) (-)": "Annualized return if you hold until the exit date in the bearish scenario.",
  "Ann. Return (Expiry) (-)": "Annualized return if you hold until expiration in the bearish scenario.",
  "Simulated Equity (-)": "Projected total equity value in the bearish scenario.",

  // Custom mode simulation columns
  "Days to Gain": "Number of days until your exit date, set via the simulation settings sliders.",
  "Underlying Gain %": "The simulated stock price change percentage, set via the simulation settings sliders.",
  "Underlying Price": "The current market price of the underlying stock.",
  "Simulated Underlying": "Projected underlying price after applying the simulated gain percentage.",
  "Simulated Premium": "Projected option premium after applying the simulated stock move and time decay.",
  "Premium % Gain": "Percentage change between your cost basis and the simulated premium.",
  "Ann. Return (Target)": "Annualized return if you hold until the exit date. Calculated as (% gain / days held) x 365.",
  "Ann. Return (Expiry)": "Annualized return if you hold until expiration.",
  "Simulated Equity": "Projected total equity value based on simulated premium and number of contracts.",
  "Simulated P&L": "Projected profit or loss in dollars based on your position size and the simulated premium.",

  // Sell metrics columns
  "Collateral Required": "Cash or margin required to hold a short (sell) position. For naked calls, this can be substantial.",
  "Return on Collateral %": "Percentage return relative to the collateral required, not the premium received.",
  "Simulated P&L (+)": "Projected profit or loss in dollars for the bullish scenario, based on your position size.",
  "Simulated P&L (-)": "Projected profit or loss in dollars for the bearish scenario, based on your position size.",
  "Max Profit": "Maximum possible profit for this position. For sold options, this is the premium received.",
  "Max Loss": "Maximum possible loss for this position. Naked calls have unlimited max loss.",

  // Greeks
  "Implied Volatility": "Market's expectation of future price movement. Higher IV means higher premiums and larger expected swings.",
  "Delta": "Sensitivity of option price to a $1 change in the underlying. Calls: 0 to 1, Puts: -1 to 0.",
  "Theta": "Daily time decay in dollars. Represents how much the option loses per day, all else equal.",
  "Gamma": "Rate of change of Delta per $1 move in the underlying. Higher near ATM and expiration.",
  "Vega": "Sensitivity of option price to a 1% change in implied volatility.",
  "Rho": "Sensitivity of option price to a 1% change in the risk-free interest rate.",
};

// Helper function to calculate days until expiration (defined outside component to avoid re-creation)
const calculateDaysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(expirationDate);
  expDate.setHours(0, 0, 0, 0);
  const diffTime = expDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

function WatchlistTable({
  items,
  selectedTickers,
  setSelectedTickers,
  selectedExpirations,
  setSelectedExpirations,
  selectedScenarios,
  setSelectedScenarios,
  simulationMode = 'preset',
  onSimulationModeChange,
  customDaysToTarget,
  customUnderlyingGain,
  onCustomSettingsChange,
  daysToExitMode = 'fixed',
  setDaysToExitMode,
  daysToExitPercent = null,
  setDaysToExitPercent,
  setCustomDaysToTarget,
  baselineMode = DEFAULT_BASELINE_MODE,
  setBaselineMode,
}) {
  // Mobile detection — combines width <768px with the landscape-phone case
  // (e.g. iPhone 14 Pro 852×393), mirroring ScreenerResponsive's predicate.
  const isCompactWidth = useIsMobile(768);
  const isLandscapePhone = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isCompactWidth || isLandscapePhone;

  const [selectedGroup, setSelectedGroup] = useState("Simulation");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Premium chart modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Last clicked ticker context for dynamic widget updates
  const { setLastClickedTicker, setLastClickedContract } = useLastClickedTicker();

  // Simulation settings state for custom mode
  const [simulationActive, setSimulationActive] = useState(false);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Favorites & removed rows
  const [favoritedRows, setFavoritedRows] = useState(new Set());
  const [removedRows, setRemovedRows] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Watchlist save modal state
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistGroups, setWatchlistGroups] = useState([]);
  const [savedContractId, setSavedContractId] = useState(null);
  const [savedContractDetails, setSavedContractDetails] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingRowKey, setSavingRowKey] = useState(null);

  // Row key helper — must be defined before useMemos that reference it
  const getRowKey = (row) => `${row['Ticker']}|${row['Option Type']}|${row['Strike']}|${row['Expiration']}`;

  // Get current column groups based on simulation mode
  const currentColumnGroups = simulationMode === 'custom' ? CUSTOM_COLUMN_GROUPS : PRESET_COLUMN_GROUPS;

  // Apply the global baseline toggle to preset-mode rows. In avg_cost mode this
  // re-derives Simulated Premium (+/-) % Change, Sell P&L (+/-), and the
  // annualized return columns against the user's cost basis. In current mode
  // this is a pass-through. Avg cost not provided => no-op (helper falls back).
  const baselineItems = useMemo(() => {
    if (!items || items.length === 0) return items;
    if (baselineMode === BASELINE_MODES.CURRENT) return items;
    return items.map(row => applyBaselineToRow(row, baselineMode));
  }, [items, baselineMode]);

  // Deduplicate items for custom mode (one row per unique contract)
  const deduplicatedItems = useMemo(() => {
    if (simulationMode !== 'custom' || !baselineItems || baselineItems.length === 0) {
      return baselineItems;
    }

    // Create a map to store unique contracts
    const contractMap = new Map();

    baselineItems.forEach(item => {
      // Create a unique key for each contract
      const key = `${item.Ticker}-${item['Option Type']}-${item.Strike}-${item.Expiration}`;

      if (!contractMap.has(key)) {
        // Map fields from preset format to custom format
        contractMap.set(key, {
          ...item,
          // Map field names for compatibility with blackScholes.js
          "Underlying Price": item["Current Underlying"] || item["Underlying Price"],
          "Days Until Expiration": calculateDaysUntilExpiration(item.Expiration),
          "Implied Volatility": item["Implied Volatility"],
        });
      }
    });

    return Array.from(contractMap.values());
  }, [baselineItems, simulationMode]);

  // Calculate max days to target from data for simulation settings.
  // Defined before processedItems because the seeded-default days-to-exit
  // is clamped to the nearest expiration (maxDaysData.days).
  const maxDaysData = useMemo(() => {
    const dataToUse = simulationMode === 'custom' ? deduplicatedItems : baselineItems;
    if (!dataToUse || dataToUse.length === 0) return { days: 365, date: null };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expirationsWithDays = dataToUse
      .map(row => row.Expiration)
      .filter(expDate => expDate && expDate !== '-')
      .map(expDate => {
        const expiration = new Date(expDate);
        expiration.setHours(0, 0, 0, 0);
        const diffTime = expiration - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return { days: diffDays, date: expDate };
      })
      .filter(item => item.days > 0)
      .sort((a, b) => a.days - b.days);

    if (expirationsWithDays.length === 0) return { days: 365, date: null };

    // Return the minimum (nearest expiration) for max slider value
    const minExpiration = expirationsWithDays[0];

    return {
      days: minExpiration.days,
      date: minExpiration.date
    };
  }, [simulationMode, deduplicatedItems, baselineItems]);

  // Apply custom simulation recalculation when in custom mode
  const processedItems = useMemo(() => {
    if (simulationMode !== 'custom') {
      return baselineItems;
    }

    // Effective custom settings. When the user hasn't touched the panel
    // sliders yet (first run of a session) the parent state is null — fall
    // back to the SimulationSettingsPanel's own defaults so the table shows
    // a real simulation immediately instead of a 0%-movement non-simulation.
    const effectiveCustomDays = customDaysToTarget
      ?? Math.min(CUSTOM_SIM_DEFAULT_DAYS, maxDaysData.days || CUSTOM_SIM_DEFAULT_DAYS);
    const effectiveCustomGain = customUnderlyingGain ?? CUSTOM_SIM_DEFAULT_GAIN;

    // Recalculate simulations with the (effective) custom settings
    return deduplicatedItems.map(item => {
      const daysUntilExp = calculateDaysUntilExpiration(item.Expiration);

      // Percentage mode: compute per-row days from each contract's expiration
      const effectiveDays = daysToExitMode === 'percent' && daysToExitPercent
        ? Math.max(1, Math.round(daysUntilExp * daysToExitPercent))
        : effectiveCustomDays;

      // Prepare the row for recalculateSimulation (needs specific field names)
      const rowForCalc = {
        "Underlying Price": item["Current Underlying"] || item["Underlying Price"],
        "Strike": item.Strike,
        "Days Until Expiration": daysUntilExp,
        "Current Premium": item["Current Premium"],
        "Implied Volatility": item["Implied Volatility"] || 30, // Default IV if not available
        "Option Type": item["Option Type"],
        "Position Type": item["Position Type"],
        "Allocated Equity": (item["Number of Contracts"] || 1) * (item["Average Cost per Contract"] || item["Current Premium"] || 0) * 100
      };

      // Use the blackScholes recalculation, passing the toggle-resolved baseline
      const baselinePremium = resolveBaseline(item, baselineMode);
      const recalculated = recalculateSimulation(rowForCalc, effectiveDays, effectiveCustomGain, 0.05, baselinePremium);

      return {
        ...item,
        "Underlying Price": item["Current Underlying"] || item["Underlying Price"],
        "Days to Gain": recalculated["Days to Gain"],
        "Underlying Gain %": recalculated["Underlying Gain %"],
        "Simulated Underlying": recalculated["Simulated Underlying"],
        "Simulated Premium": recalculated["Simulated Premium"],
        "Premium % Gain": recalculated["Premium % Gain"],
        "Simulated Equity": recalculated["Simulated Equity"],
        "Days Until Expiration": daysUntilExp,
        // Sell-specific fields (recalculateSimulation handles these for sell positions)
        "Collateral Required": recalculated["Collateral Required"],
        "Return on Collateral %": recalculated["Return on Collateral %"],
        "Max Profit": recalculated["Max Profit"],
        "Max Loss": recalculated["Max Loss"],
        "Simulated P&L": recalculated["Simulated P&L"],
        "Ann. Return (Target)": recalculated["Ann. Return (Target)"],
        "Ann. Return (Expiry)": recalculated["Ann. Return (Expiry)"],
      };
    });
  }, [simulationMode, deduplicatedItems, customDaysToTarget, customUnderlyingGain, baselineItems, daysToExitMode, daysToExitPercent, baselineMode, maxDaysData.days]);

  // Handlers for simulation settings
  const handleSimulationRefresh = (days, gain) => {
    setSimulationActive(true);
    if (onCustomSettingsChange) {
      onCustomSettingsChange(days, gain);
    }
  };

  const handleSimulationReset = () => {
    setSimulationActive(false);
    if (onCustomSettingsChange) {
      onCustomSettingsChange(null, null);
    }
    // Reset mode to fixed
    if (setDaysToExitMode) setDaysToExitMode('fixed');
    if (setDaysToExitPercent) setDaysToExitPercent(null);
  };

  const visibleItems = useMemo(() => {
    // Use processedItems for custom mode, baselineItems for preset mode so the
    // re-baselined % / P&L / ann return columns flow through to the table.
    let filtered = simulationMode === 'custom' ? processedItems : baselineItems;

    // Filter by tickers
    if (selectedTickers.length > 0) {
      filtered = filtered.filter(item => selectedTickers.includes(item.Ticker));
    }

    // Filter by expirations
    if (selectedExpirations && selectedExpirations.length > 0) {
      filtered = filtered.filter(item => selectedExpirations.includes(item.Expiration));
    }

    // Filter by scenarios
    if (selectedScenarios && selectedScenarios.length > 0) {
      filtered = filtered.filter(item =>
        selectedScenarios.includes(item["Underlying Scenario % Change"])
      );
    }

    // Filter by removed rows
    if (removedRows.size > 0) {
      filtered = filtered.filter(item => !removedRows.has(getRowKey(item)));
    }

    // Filter by favorites only
    if (showFavoritesOnly) {
      filtered = filtered.filter(item => favoritedRows.has(getRowKey(item)));
    }

    return filtered;
  }, [baselineItems, selectedTickers, selectedExpirations, selectedScenarios, simulationMode, processedItems, removedRows, showFavoritesOnly, favoritedRows]);

  if (visibleItems.length > 0) {
    const firstRowKeys = Object.keys(visibleItems[0]);
    const missingFromColumns = firstRowKeys.filter(k => !currentColumnGroups.All.includes(k));
    const missingFromData = currentColumnGroups.All.filter(c => !(c in visibleItems[0]));
    console.log("🧪 WatchlistTable Debug → First Row Keys:", firstRowKeys);
    console.log("🧪 Missing in COLUMN_GROUPS.All but in data:", missingFromColumns);
    console.log("🧪 Columns defined but missing in data:", missingFromData);
  }

  const handleGroupChange = (group) => {
    console.log(`🔄 Column Group Changed to: ${group}`);
    setSelectedGroup(group);
  };

  const visibleColumns = useMemo(() => {
    if (selectedGroup === "All") return currentColumnGroups.All;
    const groupCols = currentColumnGroups[selectedGroup] || [];
    return [...ESSENTIAL_COLUMNS, ...groupCols.filter(col => !ESSENTIAL_COLUMNS.includes(col))];
  }, [selectedGroup, currentColumnGroups]);

  const sortedData = useMemo(() => {
    const sortable = [...visibleItems];
    sortable.sort((a, b) => {
      // Tier 1: Favorites float to top
      const aFav = favoritedRows.has(getRowKey(a)) ? 0 : 1;
      const bFav = favoritedRows.has(getRowKey(b)) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;

      // Tier 2: Sort by selected column
      if (!sortConfig.key) return 0;

      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortable;
  }, [visibleItems, sortConfig, favoritedRows]);

  const csvFilteredData = useMemo(() => {
    return sortedData.map(row => {
      const filteredRow = {};
      visibleColumns.forEach(col => {
        filteredRow[col] = row[col];
      });
      return filteredRow;
    });
  }, [sortedData, visibleColumns]);

  useEffect(() => {
    if (sortedData.length > 0) {
      console.log("📋 First 3 rows of sortedData:", sortedData.slice(0, 3));
    }
  }, [sortedData]);

  useEffect(() => {
    if (visibleItems.length > 0) {
      const firstRow = visibleItems[0];
      const missingColumns = visibleColumns.filter(col => !(col in firstRow));
      if (missingColumns.length > 0) {
        console.warn("⚠ Missing columns in first row:", missingColumns);
      }
    }
  }, [visibleColumns, visibleItems]);

  const handleSort = (column) => {
    console.log(`🔀 Sorting by column: ${column}`);
    if (sortConfig.key === column) {
      setSortConfig({
        key: column,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setSortConfig({ key: column, direction: 'asc' });
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  // Handler for clicking on premium to open chart modal
  const handlePremiumClick = (row) => {
    try {
      const ticker = row['Ticker'];
      const optionType = row['Option Type']?.toLowerCase();
      const strike = row['Strike'];
      const expiration = row['Expiration'];

      if (!ticker || !optionType || !strike || !expiration) {
        console.warn('Missing contract details for premium chart');
        return;
      }

      // Look up full row from sortedData for simulated returns tab
      const fullRow = sortedData.find(r =>
        r['Ticker'] === row['Ticker'] &&
        r['Option Type'] === row['Option Type'] &&
        r['Strike'] === row['Strike'] &&
        r['Expiration'] === row['Expiration']
      );

      setSelectedContract({
        ticker,
        optionType,
        strike: parseFloat(strike),
        expiration,
        rowData: fullRow || null,
        defaultView: 'charts',
      });
      setChartModalOpen(true);
    } catch (error) {
      console.error('Error handling premium click:', error);
    }
  };

  // Handler for clicking on options contract to open options dashboard
  const handleContractClick = (row) => {
    try {
      const ticker = row['Ticker'];
      const optionType = row['Option Type']?.toLowerCase();
      const strike = row['Strike'];
      const expiration = row['Expiration'];

      if (!ticker || !optionType || !strike || !expiration) {
        console.warn('Missing contract details for options dashboard');
        return;
      }

      // Update context for dynamic widgets
      setLastClickedTicker(ticker);
      setLastClickedContract({
        option_type: optionType,
        strike: parseFloat(strike),
        expiration: expiration
      });

      // Navigate to options dashboard in new tab
      const url = `/options-dashboard/${ticker}/${optionType}/${strike}/${expiration}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error handling contract click:', error);
    }
  };

  // --- Row action helpers ---
  const handleToggleFavorite = (row) => {
    const key = getRowKey(row);
    setFavoritedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleRemoveRow = (row) => {
    const key = getRowKey(row);
    setRemovedRows(prev => new Set([...prev, key]));
    setFavoritedRows(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const handleSaveToWatchlist = async (row) => {
    const key = getRowKey(row);
    try {
      setIsSaving(true);
      setSavingRowKey(key);
      const contractData = {
        ticker: row['Ticker'],
        option_type: row['Option Type']?.toLowerCase(),
        strike: parseFloat(row['Strike']),
        expiration: row['Expiration'],
        average_cost_per_contract: parseFloat(row['Current Premium']) || undefined,
        days_to_gain: row['Days to Target'] || row['Days to Gain'] ? parseInt(row['Days to Target'] || row['Days to Gain']) : undefined,
        number_of_contracts: parseInt(row['Number of Contracts']) || 1,
      };
      const response = await saveContract(contractData);
      const newContractId = response.data.id;
      setSavedContractId(newContractId);
      setSavedContractDetails({
        ticker: row['Ticker'],
        strike: row['Strike'],
        expiration: row['Expiration'],
        numberOfContracts: parseInt(row['Number of Contracts']) || 1,
      });
      const groupsResponse = await getWatchlistGroups();
      setWatchlistGroups(groupsResponse.data || []);
      setShowWatchlistModal(true);
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Failed to save contract: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
      setSavingRowKey(null);
    }
  };

  const handleAssignToGroups = async (groupIds, mode) => {
    if (!savedContractId || groupIds.length === 0) return;
    try {
      await assignContractsToGroup(groupIds, [savedContractId], mode);
    } catch (error) {
      console.error('Error assigning to groups:', error);
    }
  };

  const fetchWatchlistGroups = async () => {
    try {
      const response = await getWatchlistGroups();
      setWatchlistGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Determine which columns should be sticky (first 6 essential columns)
  const getStickyClass = (col, index) => {
    const stickyColumns = ['Ticker', 'Position Type', 'Option Type', 'Strike', 'Current Premium', 'Expiration'];
    const stickyIndex = stickyColumns.indexOf(col);
    if (stickyIndex !== -1 && visibleColumns.includes(col)) {
      const actualIndex = visibleColumns.indexOf(col);
      if (actualIndex < 6) {
        return `table-header-cell-sticky table-header-cell-sticky-${actualIndex + 1}`;
      }
    }
    return 'table-header-cell';
  };

  const getCellStickyClass = (col, index) => {
    const stickyColumns = ['Ticker', 'Position Type', 'Option Type', 'Strike', 'Current Premium', 'Expiration'];
    const stickyIndex = stickyColumns.indexOf(col);
    if (stickyIndex !== -1 && visibleColumns.includes(col)) {
      const actualIndex = visibleColumns.indexOf(col);
      if (actualIndex < 6) {
        return `table-cell-sticky table-cell-sticky-${actualIndex + 1}`;
      }
    }
    return 'table-cell';
  };

  // Format numbers for better display
  const formatValue = (value, column) => {
    if (value === undefined || value === null || value === 'undefined') {
      return '-';
    }

    // Special handling for "Unlimited" max loss (naked calls)
    if (column === 'Max Loss' && value === 'Unlimited') {
      return '⚠️ Unlimited';
    }

    // Keep existing formatting for specific columns
    if (["Current Premium", "Average Cost per Contract"].includes(column)) {
      return Number(value).toFixed(2);
    }

    // Equity and P&L columns (both preset and custom mode)
    if (column === "Equity Invested" || column === "Simulated Equity (+)" || column === "Simulated Equity (-)" || column === "Simulated Equity"
        || column === "Simulated P&L (+)" || column === "Simulated P&L (-)" || column === "Simulated P&L") {
      const num = Number(value);
      if (!isNaN(num)) {
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }

    // Annualized return columns — cap at 5 digits, scientific notation for huge values
    if (column.startsWith('Ann. Return')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (Math.abs(num) >= 99999) {
          return `${num.toExponential(1)}%`;
        }
        return `${num.toFixed(Math.abs(num) >= 1000 ? 0 : 2)}%`;
      }
    }

    // Percentage columns (both preset and custom mode)
    if (column.includes('% Change') || column.includes('Scenario') || column === 'Premium % Gain' || column === 'Underlying Gain %' || column === 'Return on Collateral %') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)}%`;
      }
    }

    // Currency columns (premiums, underlying prices, sell-specific columns, etc.)
    if (column.includes('Premium') || column.includes('Cost') || column === 'Simulated Underlying' ||
        column === 'Underlying Price' || column === 'Current Underlying' ||
        column === 'Collateral Required' || column === 'Max Profit' || column === 'Max Loss') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (Math.abs(num) >= 1000) {
          return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return `$${num.toFixed(2)}`;
      }
    }

    // Format large numbers
    const num = parseFloat(value);
    if (!isNaN(num) && Math.abs(num) >= 10000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }

    return value;
  };

  const getValueClass = (value, column) => {
    // Color code percentage change, P&L, and annualized return columns (both preset and custom mode)
    if (column.includes('% Change') || column === 'Premium % Gain' || column === 'Return on Collateral %'
        || column === 'Simulated P&L (+)' || column === 'Simulated P&L (-)' || column === 'Simulated P&L'
        || column.startsWith('Ann. Return')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num > 0 ? 'table-cell-positive' : num < 0 ? 'table-cell-negative' : '';
      }
    }
    // Max Loss - "Unlimited" should be styled as a warning
    if (column === 'Max Loss' && value === 'Unlimited') {
      return 'text-orange-400 font-bold';
    }
    return '';
  };

  // Get data source tooltip for cells
  // Format: "P. 12/12/2025 10:54AM ET" or "E. 12/12/2025" or "def."
  const getDataSourceTooltip = (row, column) => {
    const meta = row._meta;
    if (!meta) return null;

    // Map column names to metadata keys
    const metaMap = {
      'Current Underlying': 'underlying_price',
      'Current Premium': 'current_premium',
      'Implied Volatility': 'implied_volatility',
      'Delta': 'greeks',
      'Gamma': 'greeks',
      'Theta': 'greeks',
      'Vega': 'greeks',
      'Rho': 'greeks',
      'Volume': 'volume',
    };

    const metaKey = metaMap[column];
    if (!metaKey || !meta[metaKey]) return null;

    const info = meta[metaKey];

    // Check if default value was used
    if (info.is_default) {
      return 'def. (default value used)';
    }

    // Build prefix based on data source
    let prefix = '';
    if (info.source === 'P') {
      prefix = 'P.';  // Polygon.io
    } else if (info.source === 'E') {
      prefix = 'E.';  // EODHD
    } else {
      // Unknown source - return null to show no tooltip
      return null;
    }

    // Format timestamp if available
    let timestampStr = '';
    if (info.timestamp) {
      const date = new Date(info.timestamp);
      timestampStr = ` ${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      })} ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })}`;
    }

    return `${prefix}${timestampStr}`;
  };

  // Format column headers to be more readable and allow wrapping
  const formatColumnHeader = (column) => {
    // "Current Premium" rebrands to "Avg Cost" when the global baseline
    // toggle is in avg-cost mode so the header matches what the cell shows
    // and what the simulated returns compare against.
    if (column === 'Current Premium' && baselineMode === BASELINE_MODES.AVG_COST) {
      return getStartingPriceColumnLabel(baselineMode);
    }

    // Split camelCase and add spaces
    const formatted = column
      .replace(/([A-Z])/g, ' $1')
      .replace(/([0-9]+)/g, ' $1')
      .trim();

    // Handle special cases - using \n for line breaks
    const replacements = {
      // Preset mode columns
      'Underlying Scenario % Change': 'Underlying\nScenario %',
      'Simulated Premium (+) % Change': 'Simulated\nPremium (+) %',
      'Simulated Premium (-) % Change': 'Simulated\nPremium (-) %',
      'Simulated Underlying (+)': 'Sim\nUnderlying (+)',
      'Simulated Underlying (-)': 'Sim\nUnderlying (-)',
      'Simulated Premium (+)': 'Sim\nPremium (+)',
      'Simulated Premium (-)': 'Sim\nPremium (-)',
      'Simulated Equity (+)': 'Sim\nEquity (+)',
      'Simulated Equity (-)': 'Sim\nEquity (-)',
      'Current Underlying': 'Current\nUnderlying',
      // Custom mode columns
      'Days to Gain': 'Days To\nExit',
      'Underlying Gain %': 'Underlying\nGain %',
      'Underlying Price': 'Current\nUnderlying',
      'Simulated Underlying': 'Sim\nUnderlying',
      'Simulated Premium': 'Sim\nPremium',
      'Premium % Gain': 'Premium\nGain %',
      'Simulated Equity': 'Sim\nEquity',
      'Days Until Expiration': 'Days Until\nExp',
      // Common columns
      'Number of Contracts': 'Num\nContracts',
      'Average Cost per Contract': 'Avg Cost/\nContract',
      'Days to Target': 'Days To\nExit',
      'Current Premium': 'Current\nPrem',
      'Option Type': 'Type',
      'Position Type': 'Position',
      'Implied Volatility': 'Implied\nVol',
      'Open Interest': 'Open\nInt',
      'FMV': 'Fair Mkt\nValue',
      'Equity Invested': 'Equity\nInvested',
      // Sell-specific columns
      'Collateral Required': 'Collateral\nReq',
      'Return on Collateral %': 'Return on\nCollateral %',
      'Max Profit': 'Max\nProfit',
      'Max Loss': 'Max\nLoss',
      'Simulated P&L (+)': 'Sim P&L\n(+)',
      'Simulated P&L (-)': 'Sim P&L\n(-)',
      'Simulated P&L': 'Sim\nP&L',
      // Annualized return columns (preset mode)
      'Ann. Return (Target) (+)': 'Ann. Ret.\nExit (+)',
      'Ann. Return (Target) (-)': 'Ann. Ret.\nExit (-)',
      'Ann. Return (Expiry) (+)': 'Ann. Ret.\nExpiry (+)',
      'Ann. Return (Expiry) (-)': 'Ann. Ret.\nExpiry (-)',
      // Annualized return columns (custom mode)
      'Ann. Return (Target)': 'Ann. Ret.\n(Exit)',
      'Ann. Return (Expiry)': 'Ann. Ret.\n(Expiry)'
    };

    return replacements[column] || formatted;
  };

  // Get maximum width for specific columns
  const getColumnMaxWidth = (column) => {
    const narrowColumns = ['Ticker', 'Strike', 'Option Type', 'Delta', 'Gamma', 'Theta', 'Vega', 'Rho', 'Bid', 'Ask', 'FMV'];
    const mediumColumns = ['Position Type', 'Days to Target', 'Volume', 'Open Interest', 'Max Profit', 'Max Loss', 'Simulated P&L (+)', 'Simulated P&L (-)', 'Simulated P&L',
      'Ann. Return (Target) (+)', 'Ann. Return (Target) (-)', 'Ann. Return (Expiry) (+)', 'Ann. Return (Expiry) (-)',
      'Ann. Return (Target)', 'Ann. Return (Expiry)'];
    const wideColumns = ['Expiration', 'Collateral Required', 'Return on Collateral %'];

    if (narrowColumns.includes(column)) return '70px';
    if (mediumColumns.includes(column)) return '90px';
    if (wideColumns.includes(column)) return '120px';
    return '110px';
  };

  if (!visibleItems || visibleItems.length === 0) {
    return <p className="text-muted">No watchlist items to display.</p>;
  }

  return (
    <div className="mt-10">
      {/* Simulation Mode Toggle */}
      <div className="view-toggle-container" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: isMobile ? '6px' : '12px',
        marginBottom: isMobile ? '12px' : '20px',
        padding: isMobile ? '8px' : '12px',
        background: '#1a1a1a',
        borderRadius: '8px',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => onSimulationModeChange && onSimulationModeChange('custom')}
          className={`view-toggle-btn ${simulationMode === 'custom' ? 'active' : ''}`}
          style={{
            padding: isMobile ? '6px 10px' : '8px 16px',
            borderRadius: '6px',
            border: simulationMode === 'custom' ? '2px solid #218aff' : '1px solid #333',
            background: simulationMode === 'custom' ? '#218aff20' : '#0a0a0a',
            color: simulationMode === 'custom' ? '#ffffff' : '#b0b0b0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: isMobile ? '12px' : '14px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {isMobile ? 'Details' : 'Simulation Settings'}
        </button>
        <button
          onClick={() => onSimulationModeChange && onSimulationModeChange('heatmap')}
          className={`view-toggle-btn ${simulationMode === 'heatmap' ? 'active' : ''}`}
          style={{
            padding: isMobile ? '6px 10px' : '8px 16px',
            borderRadius: '6px',
            border: simulationMode === 'heatmap' ? '2px solid #218aff' : '1px solid #333',
            background: simulationMode === 'heatmap' ? '#218aff20' : '#0a0a0a',
            color: simulationMode === 'heatmap' ? '#ffffff' : '#b0b0b0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: isMobile ? '12px' : '14px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {isMobile ? 'Heatmap' : 'Elasticity Heatmap'}
        </button>
        <button
          onClick={() => onSimulationModeChange && onSimulationModeChange('preset')}
          className={`view-toggle-btn ${simulationMode === 'preset' ? 'active' : ''}`}
          style={{
            padding: isMobile ? '6px 10px' : '8px 16px',
            borderRadius: '6px',
            border: simulationMode === 'preset' ? '2px solid #218aff' : '1px solid #333',
            background: simulationMode === 'preset' ? '#218aff20' : '#0a0a0a',
            color: simulationMode === 'preset' ? '#ffffff' : '#b0b0b0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: isMobile ? '12px' : '14px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {isMobile ? 'Preset' : 'Preset Simulations'}
        </button>
      </div>

      {/* Elasticity Heatmap View */}
      {simulationMode === 'heatmap' && (
        <WatchlistHeatmapView
          items={items}
          daysToExitMode={daysToExitMode}
          setDaysToExitMode={setDaysToExitMode}
          daysToExitPercent={daysToExitPercent}
          setDaysToExitPercent={setDaysToExitPercent}
          sharedDaysToExit={customDaysToTarget}
          setSharedDaysToExit={setCustomDaysToTarget}
          baselineMode={baselineMode}
          setBaselineMode={setBaselineMode}
          mobileVariant="heatmap"
        />
      )}

      {/* Mobile "Details" view (custom mode on mobile) — routes through
          WatchlistHeatmapView with the per-contract card-list variant. Mirrors
          the Screener mobile pattern. Desktop custom mode still uses the
          SimulationSettingsPanel + table below. */}
      {isMobile && simulationMode === 'custom' && (
        <WatchlistHeatmapView
          items={items}
          daysToExitMode={daysToExitMode}
          setDaysToExitMode={setDaysToExitMode}
          daysToExitPercent={daysToExitPercent}
          setDaysToExitPercent={setDaysToExitPercent}
          sharedDaysToExit={customDaysToTarget}
          setSharedDaysToExit={setCustomDaysToTarget}
          baselineMode={baselineMode}
          setBaselineMode={setBaselineMode}
          mobileVariant="detail"
        />
      )}

      {/* Table content - hidden when heatmap mode is active, and hidden on
          mobile when custom mode is active (replaced by the card list above). */}
      {simulationMode !== 'heatmap' && !(isMobile && simulationMode === 'custom') && (<>
      {/* Simulation Settings Panel - Only visible in custom mode */}
      {simulationMode === 'custom' && (
        <SimulationSettingsPanel
          maxDays={maxDaysData.days}
          maxExpirationDate={maxDaysData.date}
          onRefresh={handleSimulationRefresh}
          onReset={handleSimulationReset}
          isActive={simulationActive}
          defaultDays={CUSTOM_SIM_DEFAULT_DAYS}
          defaultGain={CUSTOM_SIM_DEFAULT_GAIN}
          contractCount={deduplicatedItems?.length || 0}
          label="All"
          defaultExpanded={true}
          externalDays={customDaysToTarget}
          daysToExitMode={daysToExitMode}
          daysToExitPercent={daysToExitPercent}
          onModeChange={(mode) => {
            if (setDaysToExitMode) setDaysToExitMode(mode);
          }}
          onPercentChange={(pct) => {
            if (setDaysToExitMode) setDaysToExitMode('percent');
            if (setDaysToExitPercent) setDaysToExitPercent(pct);
          }}
        />
      )}

      {/* Filters — moved here from the Watchlist.js parent so they sit below
          the toggle bar (and below SimulationSettingsPanel in custom mode),
          matching the layout used by Elasticity Heatmap mode. */}
      <WatchlistFilterControls
        data={items}
        selectedTickers={selectedTickers}
        setSelectedTickers={setSelectedTickers}
        selectedExpirations={selectedExpirations}
        setSelectedExpirations={setSelectedExpirations}
        selectedScenarios={selectedScenarios}
        setSelectedScenarios={setSelectedScenarios}
        baselineMode={baselineMode}
        setBaselineMode={setBaselineMode}
      />

      <div className="flex flex-wrap justify-between items-center mb-4">
        <div className="flex flex-wrap gap-2">
          {Object.keys(currentColumnGroups).map(group => (
            <button
              key={group}
              onClick={() => handleGroupChange(group)}
              className={`tab-button ${selectedGroup === group ? 'tab-selected' : 'tab-unselected'}`}
            >
              {group}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {/* Favorites toggle */}
          <button
            onClick={() => setShowFavoritesOnly(prev => !prev)}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
            title={showFavoritesOnly ? 'Show all results' : 'Show favorites only'}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>

          <button
            onClick={() => setShareModalOpen(true)}
            className="btn-export-primary flex items-center gap-1"
            title="Share as image"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>

          <CSVLink
            data={csvFilteredData}
            headers={visibleColumns.map(col => ({ label: col, key: col }))}
            filename={"watchlist_filtered.csv"}
            className="btn-export-primary"
          >
            Export Filtered CSV
          </CSVLink>

          <CSVLink
            data={sortedData}
            headers={COLUMN_GROUPS["All"].map(col => ({ label: col, key: col }))}
            filename={"watchlist_full.csv"}
            className="btn-export-secondary"
          >
            Export Full CSV
          </CSVLink>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {visibleColumns.map((col, index) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className={getStickyClass(col, index)}
                    style={{ maxWidth: getColumnMaxWidth(col) }}
                    {...((col === 'Current Premium'
                      ? getStartingPriceTooltip(baselineMode)
                      : COLUMN_HEADER_TOOLTIPS[col]) ? {
                      'data-tooltip-id': 'watchlist-header-tooltip',
                      'data-tooltip-content': col === 'Current Premium'
                        ? getStartingPriceTooltip(baselineMode)
                        : COLUMN_HEADER_TOOLTIPS[col],
                    } : {})}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-center whitespace-pre-line">{formatColumnHeader(col)}</span>
                      <span className="mt-1">{getSortIcon(col)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => {
                const rowKey = getRowKey(row);
                const isFavorited = favoritedRows.has(rowKey);
                const isCurrentlySaving = isSaving && savingRowKey === rowKey;

                return (
                <tr
                  key={idx}
                  className={`${idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}${isFavorited ? ' table-row-favorited' : ''}`}
                >
                  {/* Action buttons (sticky) */}
                  <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px 2px' }}>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleToggleFavorite(row)}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20
                                   flex items-center justify-center transition-all duration-200 group"
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star
                          className={`w-3 h-3 transition-colors duration-200 ${
                            isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'
                          }`}
                          fill={isFavorited ? 'currentColor' : 'none'}
                        />
                      </button>
                      <button
                        onClick={() => handleSaveToWatchlist(row)}
                        disabled={isCurrentlySaving}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#218aff] hover:bg-[#218aff]/20
                                   flex items-center justify-center transition-all duration-200 group disabled:opacity-50"
                        title="Save to Watchlist"
                      >
                        {isCurrentlySaving ? (
                          <span className="w-2.5 h-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <PlusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#218aff]" />
                        )}
                      </button>
                      <button
                        onClick={() => handleRemoveRow(row)}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20
                                   flex items-center justify-center transition-all duration-200 group"
                        title="Remove from results"
                      >
                        <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
                      </button>
                    </div>
                  </td>
                  {visibleColumns.map((col, i) => {
                    let value = row[col];

                    // Special handling for Equity Invested calculation
                    if (col === "Equity Invested") {
                      const numContracts = Number(row["Number of Contracts"]);
                      const avgCost = Number(row["Average Cost per Contract"]);
                      value = numContracts * avgCost * 100;
                    }

                    // In avg-cost mode the "Current Premium" column rebrands to
                    // "Avg Cost" — the displayed value swaps to the user's cost
                    // basis so header and cell agree. Falls back to current
                    // premium when avg cost is missing (toggle no-op).
                    if (col === 'Current Premium' && baselineMode === BASELINE_MODES.AVG_COST) {
                      const baselineVal = resolveBaseline(row, baselineMode);
                      if (baselineVal > 0) value = baselineVal;
                    }

                    const formattedValue = formatValue(value, col);
                    const valueClass = getValueClass(value, col);
                    const cellClass = getCellStickyClass(col, i);
                    const isNumber = !isNaN(parseFloat(value)) && col !== 'Ticker' && col !== 'Option Type';
                    const tooltip = getDataSourceTooltip(row, col);

                    return (
                      <td
                        key={i}
                        className={`${cellClass} ${valueClass} ${isNumber ? 'table-cell-number' : ''}`}
                        title={tooltip || undefined}
                        style={tooltip ? { cursor: 'help' } : undefined}
                      >
                        {col === 'Ticker' ? (
                          <button
                            onClick={() => handlePremiumClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition font-medium flex items-center gap-1"
                            title="View Premium & Underlying Charts"
                          >
                            {formattedValue} ↗
                          </button>
                        ) : col === 'Current Premium' ? (
                          <button
                            onClick={() => handleContractClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition flex items-center gap-1"
                            title="Open Options Dashboard"
                          >
                            {formattedValue} ↗
                          </button>
                        ) : (col === 'Current Underlying' || col === 'Underlying Price') ? (
                          <Link
                            to={`/dashboard/${row['Ticker']}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-[#218aff] hover:underline transition"
                            title="Open Ticker Dashboard"
                          >
                            {formattedValue} ↗
                          </Link>
                        ) : (
                          Array.isArray(formattedValue) ? formattedValue.join(', ') : String(formattedValue)
                        )}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* Options Premium Chart Modal */}
      <OptionsPremiumChartModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        ticker={selectedContract?.ticker}
        optionType={selectedContract?.optionType}
        strike={selectedContract?.strike}
        expiration={selectedContract?.expiration}
        rowData={selectedContract?.rowData}
        defaultView={selectedContract?.defaultView || 'charts'}
      />

      {/* Share Preview Modal */}
      <SharePreviewModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        data={sortedData}
        simulationMode={simulationMode}
        daysToTarget={customDaysToTarget}
        underlyingGain={customUnderlyingGain}
      />

      {/* Watchlist Assignment Modal */}
      <WatchlistAssignGroupsModal
        isOpen={showWatchlistModal}
        onClose={() => {
          setShowWatchlistModal(false);
          setSavedContractId(null);
          setSavedContractDetails(null);
        }}
        onAssign={handleAssignToGroups}
        groups={watchlistGroups}
        fetchGroups={fetchWatchlistGroups}
        contracts={savedContractId ? [savedContractId] : []}
        contractDetails={savedContractDetails ? [savedContractDetails] : []}
      />

      {/* Column Header Tooltips */}
      <ReactTooltip
        id="watchlist-header-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default WatchlistTable;
